/**
 * ClientTags — Coach can create, assign, and filter by color-coded tags.
 * Tags stored as JSON on coach_clients metadata via AsyncStorage (keyed per coach).
 */
import { useState, useEffect, useCallback } from 'react';
import {
  View, Text, StyleSheet, Modal, TouchableOpacity,
  TextInput, ScrollView, Alert,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useTheme } from '@/contexts/ThemeContext';
import { TYPO } from '@/constants/typography';
import { X, Plus, Tag, Check } from 'lucide-react-native';

/* ── Types ── */
export interface CoachTag {
  id: string;
  label: string;
  color: string;
}

export interface ClientTagAssignment {
  [clientId: string]: string[]; // tag ids
}

/* ── Palette ── */
const TAG_COLORS = [
  '#ef4444', '#f97316', '#eab308', '#22c55e',
  '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899',
];

/* ── Storage helpers ── */
const TAGS_KEY = (coachId: string) => `coach_tags_${coachId}`;
const ASSIGN_KEY = (coachId: string) => `coach_tag_assign_${coachId}`;

export async function loadTags(coachId: string): Promise<CoachTag[]> {
  try {
    const raw = await AsyncStorage.getItem(TAGS_KEY(coachId));
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

export async function saveTags(coachId: string, tags: CoachTag[]): Promise<void> {
  await AsyncStorage.setItem(TAGS_KEY(coachId), JSON.stringify(tags));
}

export async function loadAssignments(coachId: string): Promise<ClientTagAssignment> {
  try {
    const raw = await AsyncStorage.getItem(ASSIGN_KEY(coachId));
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

export async function saveAssignments(coachId: string, a: ClientTagAssignment): Promise<void> {
  await AsyncStorage.setItem(ASSIGN_KEY(coachId), JSON.stringify(a));
}

/* ── Tag pill (reusable) ── */
export function TagPill({ tag, onRemove }: { tag: CoachTag; onRemove?: () => void }) {
  return (
    <View style={[pillStyles.pill, { backgroundColor: tag.color + '22' }]}>
      <View style={[pillStyles.dot, { backgroundColor: tag.color }]} />
      <Text style={[pillStyles.label, { color: tag.color }]}>{tag.label}</Text>
      {onRemove && (
        <TouchableOpacity onPress={onRemove} hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}>
          <X size={12} color={tag.color} />
        </TouchableOpacity>
      )}
    </View>
  );
}
const pillStyles = StyleSheet.create({
  pill: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 2, marginRight: 4, marginBottom: 4 },
  dot: { width: 6, height: 6, borderRadius: 3 },
  label: { fontSize: 11, fontWeight: '700' },
});

/* ── Tag Manager Modal ── */
interface ManagerProps {
  visible: boolean;
  coachId: string;
  tags: CoachTag[];
  onUpdate: (tags: CoachTag[]) => void;
  onClose: () => void;
}

export function TagManager({ visible, coachId, tags, onUpdate, onClose }: ManagerProps) {
  const { theme } = useTheme();
  const c = theme.colors;
  const [newLabel, setNewLabel] = useState('');
  const [selectedColor, setSelectedColor] = useState(TAG_COLORS[0]);

  const addTag = async () => {
    const label = newLabel.trim();
    if (!label) return;
    if (tags.some((t) => t.label.toLowerCase() === label.toLowerCase())) {
      Alert.alert('Duplicate', 'Tag already exists');
      return;
    }
    const tag: CoachTag = {
      id: Date.now().toString(36) + Math.random().toString(36).slice(2, 5),
      label,
      color: selectedColor,
    };
    const next = [...tags, tag];
    await saveTags(coachId, next);
    onUpdate(next);
    setNewLabel('');
  };

  const removeTag = async (id: string) => {
    const next = tags.filter((t) => t.id !== id);
    await saveTags(coachId, next);
    onUpdate(next);
  };

  return (
    <Modal visible={visible} animationType="slide" transparent>
      <View style={styles.overlay}>
        <View style={[styles.modal, { backgroundColor: c.background, borderColor: c.border }]}>
          <View style={styles.header}>
            <Text style={[styles.title, { color: c.text, fontFamily: TYPO.heading.bold }]}>MANAGE TAGS</Text>
            <TouchableOpacity onPress={onClose}><X size={22} color={c.textSecondary} /></TouchableOpacity>
          </View>

          {/* Existing tags */}
          <ScrollView style={{ maxHeight: 200, marginBottom: 12 }}>
            {tags.length === 0 && (
              <Text style={{ color: c.textTertiary, fontSize: 13, textAlign: 'center', paddingVertical: 16 }}>
                No tags yet. Create one below.
              </Text>
            )}
            {tags.map((tag) => (
              <View key={tag.id} style={[styles.tagRow, { borderColor: c.border }]}>
                <View style={[styles.colorDot, { backgroundColor: tag.color }]} />
                <Text style={[styles.tagLabel, { color: c.text }]}>{tag.label}</Text>
                <TouchableOpacity onPress={() => removeTag(tag.id)}>
                  <X size={16} color={c.textTertiary} />
                </TouchableOpacity>
              </View>
            ))}
          </ScrollView>

          {/* Color picker */}
          <View style={styles.colorRow}>
            {TAG_COLORS.map((clr) => (
              <TouchableOpacity
                key={clr}
                style={[styles.colorSwatch, { backgroundColor: clr, borderWidth: selectedColor === clr ? 2 : 0, borderColor: c.text }]}
                onPress={() => setSelectedColor(clr)}
              />
            ))}
          </View>

          {/* New tag input */}
          <View style={styles.inputRow}>
            <TextInput
              style={[styles.input, { color: c.text, backgroundColor: c.surface, borderColor: c.border }]}
              placeholder="New tag name..."
              placeholderTextColor={c.textTertiary}
              value={newLabel}
              onChangeText={setNewLabel}
              maxLength={24}
            />
            <TouchableOpacity
              style={[styles.addBtn, { backgroundColor: newLabel.trim() ? selectedColor : c.border }]}
              onPress={addTag}
              disabled={!newLabel.trim()}
            >
              <Plus size={18} color="#fff" />
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

/* ── Assign Tags to Client Modal ── */
interface AssignProps {
  visible: boolean;
  coachId: string;
  clientId: string;
  clientName: string;
  tags: CoachTag[];
  assignments: ClientTagAssignment;
  onUpdate: (a: ClientTagAssignment) => void;
  onClose: () => void;
}

export function AssignClientTags({ visible, coachId, clientId, clientName, tags, assignments, onUpdate, onClose }: AssignProps) {
  const { theme } = useTheme();
  const c = theme.colors;
  const assigned = assignments[clientId] ?? [];

  const toggle = async (tagId: string) => {
    const current = assignments[clientId] ?? [];
    const next = current.includes(tagId) ? current.filter((id) => id !== tagId) : [...current, tagId];
    const updated = { ...assignments, [clientId]: next };
    await saveAssignments(coachId, updated);
    onUpdate(updated);
  };

  return (
    <Modal visible={visible} animationType="fade" transparent>
      <View style={styles.overlay}>
        <View style={[styles.modal, { backgroundColor: c.background, borderColor: c.border }]}>
          <View style={styles.header}>
            <View>
              <Text style={[styles.title, { color: c.text, fontFamily: TYPO.heading.bold }]}>TAGS</Text>
              <Text style={{ color: c.textSecondary, fontSize: 12 }}>{clientName}</Text>
            </View>
            <TouchableOpacity onPress={onClose}><X size={22} color={c.textSecondary} /></TouchableOpacity>
          </View>
          <ScrollView style={{ maxHeight: 300 }}>
            {tags.length === 0 && (
              <Text style={{ color: c.textTertiary, fontSize: 13, textAlign: 'center', paddingVertical: 16 }}>
                No tags created yet. Manage tags first.
              </Text>
            )}
            {tags.map((tag) => {
              const isAssigned = assigned.includes(tag.id);
              return (
                <TouchableOpacity key={tag.id} style={[styles.tagRow, { borderColor: c.border }]} onPress={() => toggle(tag.id)}>
                  <View style={[styles.colorDot, { backgroundColor: tag.color }]} />
                  <Text style={[styles.tagLabel, { color: c.text }]}>{tag.label}</Text>
                  {isAssigned && <Check size={16} color={tag.color} />}
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

/* ── Tag Filter Bar ── */
interface FilterProps {
  tags: CoachTag[];
  activeTagId: string | null;
  onSelect: (tagId: string | null) => void;
}

export function TagFilterBar({ tags, activeTagId, onSelect }: FilterProps) {
  const { theme } = useTheme();
  const c = theme.colors;
  if (tags.length === 0) return null;

  return (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.filterBar} contentContainerStyle={{ paddingHorizontal: 16, gap: 6 }}>
      <TouchableOpacity
        style={[styles.filterChip, { backgroundColor: activeTagId === null ? c.accent + '22' : c.surface, borderColor: c.border }]}
        onPress={() => onSelect(null)}
      >
        <Text style={[styles.filterChipText, { color: activeTagId === null ? c.accent : c.textSecondary }]}>All</Text>
      </TouchableOpacity>
      {tags.map((tag) => (
        <TouchableOpacity
          key={tag.id}
          style={[styles.filterChip, { backgroundColor: activeTagId === tag.id ? tag.color + '22' : c.surface, borderColor: activeTagId === tag.id ? tag.color : c.border }]}
          onPress={() => onSelect(activeTagId === tag.id ? null : tag.id)}
        >
          <View style={[pillStyles.dot, { backgroundColor: tag.color }]} />
          <Text style={[styles.filterChipText, { color: activeTagId === tag.id ? tag.color : c.textSecondary }]}>{tag.label}</Text>
        </TouchableOpacity>
      ))}
    </ScrollView>
  );
}

/* ── Styles ── */
const styles = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.7)', justifyContent: 'center', alignItems: 'center' },
  modal: { borderRadius: 2, borderWidth: 1, padding: 20, width: '90%', maxWidth: 400 },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  title: { fontSize: 16, fontWeight: '900', letterSpacing: 1 },
  tagRow: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 10, borderBottomWidth: StyleSheet.hairlineWidth },
  colorDot: { width: 12, height: 12, borderRadius: 6 },
  tagLabel: { flex: 1, fontSize: 14, fontWeight: '600' },
  colorRow: { flexDirection: 'row', gap: 8, marginBottom: 12, flexWrap: 'wrap' },
  colorSwatch: { width: 28, height: 28, borderRadius: 14 },
  inputRow: { flexDirection: 'row', gap: 8 },
  input: { flex: 1, borderWidth: 1, borderRadius: 2, padding: 10, fontSize: 14 },
  addBtn: { width: 40, height: 40, borderRadius: 2, alignItems: 'center', justifyContent: 'center' },
  filterBar: { marginBottom: 8, maxHeight: 40 },
  filterChip: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 10, paddingVertical: 6, borderRadius: 2, borderWidth: 1 },
  filterChipText: { fontSize: 12, fontWeight: '700' },
});
